import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db, auth } from "./firebase";
import { 
  collection, 
  addDoc, 
  serverTimestamp
} from "firebase/firestore";
import { checkEmailInAllCollections } from './utils/emailValidator';
import { useNavigate } from "react-router-dom";
import EmailNotificationService from './services/emailNotifications';

function Registration() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userEmail, setUserEmail] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    emailAddress: "",
    phoneNumber: "",
    programFormat: "On-site training",
    paymentPlan: "No, one-time payment",
    trainingTime: "Morning",
    classDays: "Weekdays",
    expectations: [],
    recommendations: "",
  });

  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const email = currentUser.email;
      setUserEmail(email);
      setFormData(prev => ({ ...prev, emailAddress: email }));

      // Check for existing accounts across all collections
      const checkExistingAccounts = async () => {
        try {
          const existingAccounts = await checkEmailInAllCollections(email);
          
          if (existingAccounts.length > 0) {
            const accountTypes = existingAccounts.map(acc => {
              switch(acc.type) {
                case 'investment': return 'VonScalper Copy Trading';
                case 'training': return 'Training Program';
                case 'general': return 'Investment Account';
                default: return acc.type;
              }
            }).join(', ');
            
            setErrors(prev => ({
              ...prev,
              submit: `You already have accounts for: ${accountTypes}. Please use the dashboard selection to access your services.`
            }));
            setSubmissionStatus("existing_account");
            
            setTimeout(() => {
              const goToDashboard = window.confirm(
                `You have existing accounts for: ${accountTypes}\n\nWould you like to go to your dashboard selection?`
              );
              
              if (goToDashboard) {
                navigate('/dashboard-selection');
              }
            }, 2000);
          }
        } catch (error) {
          console.error("Error checking existing accounts:", error);
        }
      };

      checkExistingAccounts();
    } else {
      navigate('/auth');
    }
  }, [navigate]);

  const questions = [
    {
      text: `Welcome to our Training Program, ${userEmail ? userEmail.split('@')[0] : 'Future Trader'}! Let's start with your full name`,
      name: "name",
      type: "text",
      required: true,
    },
    {
      text: "Confirm your email address",
      name: "emailAddress",
      type: "email-readonly",
      required: true,
      note: "This email is linked to your account and cannot be changed"
    },
    {
      text: "And your phone number?",
      name: "phoneNumber",
      type: "tel",
      required: true,
    },
    {
      text: "Which program format would you prefer?",
      name: "programFormat",
      type: "select",
      required: true,
      options: [
        {
          value: "On-site training",
          label: "On-site training (physical location)",
        },
        {
          value: "Online training",
          label: "Online training (virtual classes)",
        },
      ],
    },
    {
      text: "What are your expectations from this program?",
      name: "expectations",
      type: "checkbox",
      required: true,
      options: [
        { 
          value: "Learn algorithmic trading",
          label: "Master algorithmic trading concepts and strategies" 
        },
        { 
          value: "Build AI trading systems",
          label: "Learn to build AI-powered trading systems" 
        },
        { 
          value: "Career transition",
          label: "Transition into quantitative trading career" 
        },
        { 
          value: "Fund management",
          label: "Manage live trading funds" 
        },
        { 
          value: "Network",
          label: "Network with like-minded traders" 
        },
      ],
    },
    {
      text: "When would you prefer to attend classes?",
      name: "trainingTime",
      type: "select",
      required: true,
      options: [
        { value: "Morning", label: "Morning (9 AM–12 PM)" },
        { value: "Afternoon", label: "Afternoon (1 PM–4 PM)" },
        { value: "Evening", label: "Evening (6 PM–9 PM)" },
      ],
    },
    {
      text: "Which days work best for you?",
      name: "classDays",
      type: "select",
      required: true,
      options: [
        { value: "Weekdays", label: "Weekdays" },
        { value: "Weekends", label: "Weekends" },
      ],
    },
    {
      text: "How would you like to handle the payment?",
      name: "paymentPlan",
      type: "select",
      required: true,
      options: [
        {
          value: "Yes, monthly installments",
          label: "Monthly installments",
        },
        {
          value: "Yes, two installments (50/50)",
          label: "Two installments (50/50)",
        },
        { value: "No, one-time payment", label: "One-time payment" },
      ],
    },
    {
      text: "Any recommendations or special requests for the program?",
      name: "recommendations",
      type: "textarea",
      required: false,
      placeholder: "Share your thoughts on how we can make this program even better for you..."
    },
  ];

  const handleNext = async () => {
    const currentField = questions[currentQuestion];
    const value = formData[currentField.name];
    
    const error = validateField(
      currentField.name, 
      value, 
      currentField.type
    );
    
    if (error) {
      setErrors(prev => ({ ...prev, [currentField.name]: error }));
      return;
    }

    if (currentField.name === 'emailAddress') {
      // Just move to next question
    }

    setErrors(prev => ({ ...prev, [currentField.name]: '' }));
    setCurrentQuestion(prev => prev + 1);
  };

  const validateField = (name, value, type) => {
    switch (type) {
      case "text":
        if (!value || !value.trim()) {
          return "This field is required";
        }
        return "";

      case "email-readonly":
        return "";

      case "tel":
        if (!value || !value.trim()) {
          return "Phone number is required";
        }
        const phoneRegex = /^\+?[\d\s-]{10,}$/;
        if (!phoneRegex.test(value.replace(/\s/g, ""))) {
          return "Please enter a valid phone number";
        }
        return "";

      case "checkbox":
        if (!value || value.length === 0) {
          return "Please select at least one option";
        }
        return "";

      case "select":
        if (!value) {
          return "Please select an option";
        }
        return "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'emailAddress') {
      return;
    }
    
    if (type === "checkbox") {
      const updatedExpectations = checked
        ? [...formData.expectations, value]
        : formData.expectations.filter(item => item !== value);
      
      setFormData(prev => ({
        ...prev,
        expectations: updatedExpectations
      }));
      
      const error = validateField(name, updatedExpectations, type);
      setErrors(prev => ({ ...prev, [name]: error }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      const error = validateField(name, value, type);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    questions.forEach(question => {
      if (question.required) {
        const error = validateField(
          question.name,
          formData[question.name],
          question.type
        );
        if (error) {
          newErrors[question.name] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Removed unused function checkExistingRegistration - functionality moved to checkEmailInAllCollections


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmissionStatus("submitting");

    try {
      // Check for existing accounts across all collections
      const existingAccounts = await checkEmailInAllCollections(formData.emailAddress);
      
      if (existingAccounts.length > 0) {
        const accountTypes = existingAccounts.map(acc => {
          switch(acc.type) {
            case 'investment': return 'VonScalper Copy Trading';
            case 'training': return 'Training Program';
            case 'general': return 'Investment Account';
            default: return acc.type;
          }
        }).join(', ');
        
        setErrors(prev => ({
          ...prev,
          submit: `You already have accounts for: ${accountTypes}. Please use the dashboard selection to access your services.`
        }));
        setSubmissionStatus("existing_account");
        
        setTimeout(() => {
          navigate('/dashboard-selection');
        }, 3000);
        
        return;
      }

      // Store in Firestore with authenticated user's UID
      const registrationData = {
        ...formData,
        firebaseUid: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        status: 'pending',
        submittedAt: new Date().toISOString(),
        lastUpdated: serverTimestamp()
      };

      const questionnaireCollection = collection(db, "FormRegistration");
      const docRef = await addDoc(questionnaireCollection, registrationData);

      console.log("Registration submitted with ID: ", docRef.id);
      localStorage.setItem('registrationId', docRef.id);
      localStorage.setItem('userEmail', formData.emailAddress);

      // Send email notification
      try {
        await EmailNotificationService.notifyTrainingRegistration({
          firstName: formData.name.split(' ')[0] || formData.name,
          lastName: formData.name.split(' ').slice(1).join(' ') || '',
          emailAddress: formData.emailAddress,
          phoneNumber: formData.phoneNumber,
          programFormat: formData.programFormat,
          trainingTime: formData.trainingTime,
          registrationId: docRef.id
        });
        console.log('Email notification sent successfully');
      } catch (emailError) {
        console.error('Failed to send email notification:', emailError);
        // Don't break the flow if email fails
      }

      setSubmissionStatus("success");
      navigate("/academy/dashboard");
      
    } catch (error) {
      console.error("Error submitting registration:", error);
      setSubmissionStatus("error");
      setErrors(prev => ({
        ...prev,
        submit: error.message || "Failed to submit registration. Please try again."
      }));
    }
  };

  const renderField = (question) => {
    const error = errors[question.name];
    
    const inputClasses = `w-full border ${
      error ? 'border-red-500' : 'border-gray-300'
    } px-4 py-3 rounded-lg text-black bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200`;

    const readOnlyClasses = `w-full border border-gray-400 px-4 py-3 rounded-lg text-gray-600 bg-gray-100 cursor-not-allowed`;

    switch (question.type) {
      case "text":
      case "tel":
        return (
          <div>
            <input
              type={question.type}
              name={question.name}
              value={formData[question.name]}
              onChange={handleChange}
              className={inputClasses}
              placeholder={`Enter your ${question.text.toLowerCase()}`}
            />
            {error && (
              <div className="text-red-500 text-sm mt-2">{error}</div>
            )}
          </div>
        );

      case "email-readonly":
        return (
          <div className="space-y-2">
            <input
              type="email"
              name={question.name}
              value={formData[question.name]}
              readOnly
              className={readOnlyClasses}
            />
            <div className="flex items-center gap-2 text-blue-400 text-sm">
              <span>🔒</span> 
              <span>{question.note}</span>
            </div>
            {error && (
              <div className="text-red-500 text-sm mt-2">{error}</div>
            )}
          </div>
        );

      case "select":
        return (
          <div>
            <select
              name={question.name}
              value={formData[question.name]}
              onChange={handleChange}
              className={inputClasses}
            >
              {question.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {error && (
              <div className="text-red-500 text-sm mt-2">{error}</div>
            )}
          </div>
        );

      case "checkbox":
        return (
          <div>
            {question.options.map((option) => (
              <div key={option.value} className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id={option.value}
                  name={question.name}
                  value={option.value}
                  checked={formData.expectations.includes(option.value)}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label 
                  htmlFor={option.value} 
                  className="ml-3 text-sm font-medium text-white"
                >
                  {option.label}
                </label>
              </div>
            ))}
            {error && (
              <div className="text-red-500 text-sm mt-2">{error}</div>
            )}
          </div>
        );

      case "textarea":
        return (
          <div>
            <textarea
              name={question.name}
              value={formData[question.name]}
              onChange={handleChange}
              className={`${inputClasses} h-32 resize-none`}
              placeholder={question.placeholder}
            />
            {error && (
              <div className="text-red-500 text-sm mt-2">{error}</div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="container mx-auto p-4 flex flex-col items-center justify-center min-h-screen"
    >
      <div className="w-full max-w-2xl bg-black/20 backdrop-blur-md rounded-xl p-4 sm:p-8">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-white font-orbitron text-center"
        >
          Join Our Training Program
        </motion.h1>

        <div className="mb-8">
          <div className="relative pt-1">
            <div className="flex justify-between items-center mb-4">
              <span className="text-white font-orbitron text-sm sm:text-lg">
                Step {currentQuestion + 1} of {questions.length}
              </span>
              <span className="text-blue-400 font-orbitron text-sm sm:text-base">
                {Math.round(((currentQuestion + 1) / questions.length) * 100)}%
              </span>
            </div>
            <div className="relative h-2 bg-blue-900/30 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-blue-400"
              />
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="mb-8"
          >
            <h2 className="text-lg sm:text-xl font-medium mb-4 text-white font-orbitron">
              {questions[currentQuestion].text}
              {questions[currentQuestion].required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </h2>
            {renderField(questions[currentQuestion])}
          </motion.div>
        </AnimatePresence>

        <motion.div className="flex justify-between mt-8">
          {currentQuestion > 0 && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentQuestion(prev => prev - 1)}
              className="px-4 sm:px-6 py-3 text-sm sm:text-base bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              Previous
            </motion.button>
          )}
          
          {currentQuestion < questions.length - 1 ? (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNext}
              className="px-4 sm:px-6 py-3 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 ml-auto"
            >
              Next
            </motion.button>
          ) : (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSubmit}
              disabled={submissionStatus === "submitting"}
              className="px-4 sm:px-6 py-3 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 ml-auto font-orbitron"
            >
              {submissionStatus === "submitting" ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                "Complete Registration"
              )}
            </motion.button>
          )}
        </motion.div>

        {submissionStatus === "error" && !errors.submit?.includes('already have accounts') && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 text-red-500 text-center bg-red-100/10 p-3 rounded-lg"
          >
            {errors.submit || "An error occurred. Please try again."}
          </motion.div>
        )}

        {submissionStatus === "existing_account" && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 text-blue-400 text-center bg-blue-500/10 border border-blue-500/20 p-4 rounded-lg backdrop-blur-sm"
          >
            <span className="text-2xl mr-2">ℹ️</span>
            <div>
              <p className="font-semibold mb-2">Account Already Exists!</p>
              <p className="text-sm mb-4">{errors.submit}</p>
              <div className="flex justify-center space-x-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/dashboard-selection')}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all font-semibold"
                >
                  📊 Go to My Accounts
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/auth')}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-all font-semibold"
                >
                  🔐 Sign In Instead
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default Registration;